<template>
  <div class="app-container">
    <!-- Left column for preview -->
    <div class="left-panel">
      <div class="top-controls">
        <div class="upload-buttons">
          <el-upload class="upload-component" action="" :auto-upload="false" :show-file-list="false"
            :on-change="handleFileChange" accept=".pdf,.jpg,.jpeg,.png">
            <el-button type="primary">
              <i class="el-icon-upload"></i> Upload File
            </el-button>
          </el-upload>
          <el-button type="success" @click="openCamera">
            <i class="el-icon-camera"></i> 拍照上传
          </el-button>
          <input ref="cameraInput" type="file" accept="image/*" capture="environment" @change="handleCameraChange"
            style="display:none" />
        </div>
        <div v-if="pdfUrl && fileType === 'pdf'" class="page-controls">
          <el-button :disabled="currentPage <= 1" @click="currentPage--">Previous</el-button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <el-button :disabled="currentPage >= totalPages" @click="currentPage++">Next</el-button>
        </div>
      </div>

      <!-- File Preview with scroll -->
      <div class="preview-scroll-container">
        <div class="preview-container">
          <!-- PDF Preview -->
          <pdf v-if="pdfUrl && fileType === 'pdf'" :src="pdfUrl" :page="currentPage" :scale="5.5" :quality="6"
            :disable-range="false" :render-text="true" @num-pages="pageLoaded" style="transform-origin: top center;">
          </pdf>
          <!-- Image Preview -->
          <img v-if="imageUrl && fileType === 'image'" :src="imageUrl" class="image-preview" alt="Uploaded image">
        </div>
      </div>
    </div>

    <!-- Right column for OCR results -->
    <div class="right-panel">
      <div class="brand-header">
        <div class="brand-left">
          <img src="./assets/logo.png" class="brand-logo" alt="Green AI Logo">
          <div class="brand-text">
            <div class="brand-title">Green AI</div>
            <div class="brand-subtitle">智慧文檔識別平台</div>
          </div>
        </div>
      </div>


      <div v-if="loading" class="status-message">
        <i class="el-icon-loading"></i>
        <div class="loading-text">{{ loadingText }}</div>
        <div class="loading-tip" v-if="loadingText.includes('Processing')">
          Please keep this page open. Large files may take several minutes to process.
        </div>
      </div>
      <div v-else-if="error" class="status-message error">
        <i class="el-icon-error"></i>
        {{ error }}
      </div>
      <div v-else-if="filteredResults.length" class="ocr-results">
        <div class="debug-info">
          <div>
            當前第 {{ currentPage }} 頁，共 {{ totalPages }} 頁
            <br>
            本頁包含 {{ filteredResults.length }} 條識別結果
          </div>
          <div class="debug-actions">
            <el-button type="primary" size="small" @click="copyCurrentPageContent">
              <i class="el-icon-copy-document"></i> 複製當頁
            </el-button>
            <el-button size="small" :disabled="!ocrResults.length" @click="copyAllContent">
              <i class="el-icon-document-copy"></i> 複製全部
            </el-button>
            <el-button size="small" @click="scrollRightPanelToTop">
              <i class="el-icon-top"></i> 回到頂部
            </el-button>
          </div>
        </div>
        <div v-for="(result, index) in filteredResults" :key="index" class="ocr-result-item">
          <div class="result-text">{{ result.text }}</div>
        </div>
      </div>
      <div v-else class="status-message">
        <i class="el-icon-upload2"></i>
        Upload a file to see OCR results
      </div>
    </div>
  </div>
</template>

<script>
import pdf from 'vue-pdf'

export default {
  name: 'App',
  components: {
    pdf
  },
  data() {
    return {
      pdfUrl: null,
      imageUrl: null,
      fileType: null,
      loading: false,
      loadingText: '',
      error: null,
      currentPage: 1,
      pageCount: 0,
      pdfTotalPages: 0,
      ocrResults: [], // 存儲OCR結果的二維陣列
    }
  },
  computed: {
    filteredResults() {
      if (!this.ocrResults.length) return [];

      // 返回當前頁的資料（頁碼從1開始，陣列索引從0開始）
      const pageIndex = this.currentPage - 1;
      if (pageIndex < this.ocrResults.length) {
        return this.ocrResults[pageIndex].filter(text => text.trim() !== '').map(text => ({
          text: text
        }));
      }
      return [];
    },
    totalPages() {
      if (this.fileType === 'pdf') {
        // 使用OCR結果的頁數和PDF頁數中的較大值
        return Math.max(this.pdfTotalPages, this.ocrResults.length);
      }
      return 1; // 圖片始終是1頁
    }
  },
  methods: {
    openCamera() {
      // iOS/iPadOS Safari、Android Chrome 會拉起相機/相簿選擇
      const input = this.$refs.cameraInput
      if (input && input.click) input.click()
    },
    handleCameraChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      // 複用已有的上傳邏輯
      this.handleFileChange({ name: file.name, raw: file })
      // 清空 value 以便下次可重複選擇同一檔案
      e.target.value = ''
    },
    copyAllContent() {
      if (!this.ocrResults.length) {
        this.$message.warning('沒有可複製的內容');
        return;
      }
      // 将所有页的文本合并
      const all = this.ocrResults
        .map(page => (Array.isArray(page) ? page.filter(t => (t || '').trim() !== '') : []))
        .map(arr => arr.join('\n'))
        .join('\n\n');

      try {
        navigator.clipboard.writeText(all);
        this.$message.success('全部內容已複製到剪貼簿');
      } catch (e) {
        // 兜底
        const ta = document.createElement('textarea');
        ta.value = all;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.$message.success('全部內容已複製到剪貼簿');
      }
    },
    scrollRightPanelToTop() {
      try {
        const list = this.$el.querySelector('.ocr-results');
        if (list) {
          if (typeof list.scrollTo === 'function') {
            list.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            list.scrollTop = 0;
          }
          return;
        }
        const panel = this.$el.querySelector('.right-panel');
        if (panel) {
          if (typeof panel.scrollTo === 'function') {
            panel.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            panel.scrollTop = 0;
          }
        }
      } catch (_) { }
    },
    handleFileChange(file) {
      if (!file) return

      // Reset previous state
      this.pdfUrl = null
      this.imageUrl = null
      this.fileType = null
      this.currentPage = 1
      this.pageCount = 0
      this.pdfTotalPages = 0
      this.ocrResults = []

      const fileName = file.name.toLowerCase()
      if (fileName.endsWith('.pdf')) {
        this.fileType = 'pdf'
        this.pdfUrl = URL.createObjectURL(file.raw)
      } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) {
        this.fileType = 'image'
        this.imageUrl = URL.createObjectURL(file.raw)
      } else {
        this.error = 'Unsupported file type. Please upload PDF or image files (JPG, PNG).'
        return
      }

      this.uploadFile(file.raw)
    },
    async uploadFile(file) {
      this.loading = true
      this.error = null
      this.ocrResults = []
      this.loadingText = 'Uploading file...'

      try {
        const formData = new FormData()
        formData.append('file', file)
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 300000)

        this.loadingText = 'Processing document... This may take a few minutes.'
        const response = await fetch('/api/image-ocr/upload', {
          method: 'POST',
          body: formData,
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
        }

        this.loadingText = 'Receiving results...'
        const data = await response.json()

        if (data.code === 200 && data.data && Array.isArray(data.data)) {
          // 處理新的 API 返回格式：每個結果是一個包含 confidence, box, text 的物件
          // 將二維陣列中的每個物件提取出 text 欄位
          this.ocrResults = data.data.map(page => {
            if (!Array.isArray(page)) return [];
            return page
              .filter(item => item && item.text && item.text.trim() !== '')
              .map(item => item.text);
          });
          console.log('Total pages in OCR results:', this.ocrResults.length);

          // 更新頁面計數
          this.pageCount = this.totalPages;
        } else {
          throw new Error('Invalid response format')
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          this.error = 'Request timed out after 5 minutes. Please try again.'
        } else {
          this.error = 'Failed to process document: ' + err.message
        }
      } finally {
        this.loading = false
        this.loadingText = ''
      }
    },
    pageLoaded(pdfPageCount) {
      // 更新 PDF 實際頁數
      this.pdfTotalPages = pdfPageCount;
      // 更新總頁數
      this.pageCount = this.totalPages;
    },
    async copyCurrentPageContent() {
      if (!this.filteredResults.length) {
        this.$message.warning('當前頁沒有內容可複製');
        return;
      }

      // 将当前页的所有OCR结果合并为一个字符串
      const content = this.filteredResults.map(result => result.text).join('\n');

      try {
        // 使用現代瀏覽器的 Clipboard API
        await navigator.clipboard.writeText(content);
        this.$message.success('當前頁內容已複製到剪貼簿');
      } catch (err) {
        // 如果 Clipboard API 不可用，使用備用方法
        try {
          const textArea = document.createElement('textarea');
          textArea.value = content;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          this.$message.success('當前頁內容已複製到剪貼簿');
        } catch (fallbackErr) {
          this.$message.error('複製失敗，請手動選擇複製');
          console.error('複製失敗:', fallbackErr);
        }
      }
    }
  }
}
</script>

<style>
.theme-gradient {
  background: linear-gradient(135deg, #0a8f7b 0%, #11b59a 100%);
}

:root {
  --brand-color: #0a8f7b;
  --brand-accent: #11b59a;
  --brand-fg: #0b3d36;
}

.app-container {
  display: flex;
  min-height: 100vh;
  padding: 20px;
  gap: 20px;
  background-color: #f5f7fa;
}

/* 响应式：平板以下改为上下布局 */
@media (max-width: 1024px) {
  .app-container {
    flex-direction: column;
  }

  .left-panel {
    max-width: 100%;
  }
}

/* 手机端：压缩边距与字体，按钮换行 */
@media (max-width: 768px) {
  .app-container {
    padding: 12px;
    gap: 12px;
  }

  .top-controls {
    padding-bottom: 12px;
  }

  .upload-buttons {
    gap: 8px;
  }

  .right-panel {
    padding: 14px 14px 12px;
  }

  .brand-header {
    margin-bottom: 10px;
  }

  .ocr-results {
    max-height: calc(100vh - 180px);
  }
}

.left-panel {
  width: 100%;
  max-width: 800px;
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.top-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.upload-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Camera upload button (success color aligns with brand green) */
.upload-buttons .el-button.el-button--success {
  background-color: var(--brand-color);
  border-color: var(--brand-color);
}

.upload-buttons .el-button.el-button--success:hover {
  filter: brightness(1.05);
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.page-info {
  min-width: 80px;
  text-align: center;
  color: #606266;
  flex: none;
}

.preview-scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.preview-scroll-container::-webkit-scrollbar {
  width: 8px;
}

.preview-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.preview-scroll-container::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.preview-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

/* 调整预览容器的内边距，让内容更紧凑 */
.preview-container {
  justify-content: center;
  padding: 10px;
  min-height: min-content;
}

/* 确保 PDF 和图片在容器中居中且不超出边界 */
.preview-container pdf {
  max-width: 100%;
  margin: 0 auto;
}

.image-preview {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  margin: 0 auto;
}

.right-panel {
  flex: 1;
  /* 让右侧面板占据剩余空间 */
  min-width: 320px;
  /* 保持最小宽度 */
  background-color: #ffffff;
  border-radius: 10px;
  padding: 20px 20px 16px;
  overflow-y: auto;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #eef2f5;
}

.status-message {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.status-message i {
  display: block;
  font-size: 36px;
  margin-bottom: 10px;
}

.status-message.error {
  color: #f56c6c;
}

.loading-text {
  margin: 10px 0;
  font-size: 16px;
}

.loading-tip {
  margin-top: 10px;
  font-size: 14px;
  color: #909399;
  padding: 10px;
  background-color: #f4f4f5;
  border-radius: 4px;
}

.ocr-results {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 2px 8px 2px;
  max-height: calc(100vh - 140px);
  /* 设置最大高度 */
  overflow-y: auto;
  /* 添加垂直滚动 */
}

.ocr-result-item {
  padding: 12px 14px;
  border: 1px solid #e8edf1;
  border-radius: 8px;
  background-color: #fafcfd;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  word-break: break-all;
}

.ocr-result-item:hover {
  transform: translateY(-2px);
  border-color: var(--brand-accent);
  box-shadow: 0 8px 20px rgba(10, 143, 123, 0.08);
}

.result-text {
  color: #303133;
  line-height: 1.5;
  word-break: break-word;
}

.debug-info {
  position: sticky;
  top: 0;
  background: rgba(10, 143, 123, 0.06);
  z-index: 1;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid rgba(10, 143, 123, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

.debug-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.debug-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Brand header */
.brand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 12px;
  border: 1px solid rgba(10, 143, 123, 0.12);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(10, 143, 123, 0.06), rgba(17, 181, 154, 0.06));
}

.brand-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-logo {
  height: 28px;
  width: auto;
}

.brand-text {
  line-height: 1.2;
}

.brand-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-fg);
}

.brand-subtitle {
  font-size: 12px;
  color: #909399;
}

.brand-actions .el-button {
  padding: 6px 10px;
}

/* Panel actions bar */
.panel-actions {
  display: flex;
  gap: 8px;
  margin: 6px 0 12px;
}

.panel-actions .el-button {
  border-color: var(--brand-color);
  color: var(--brand-color);
}

.panel-actions .el-button:hover {
  background: var(--brand-color);
  color: #fff;
}

/* Page controls micro-interactions */
.page-controls .el-button {
  border-color: var(--brand-color);
  color: var(--brand-color);
}

.page-controls .el-button:hover {
  background: var(--brand-color);
  color: #fff;
}

.page-info {
  color: var(--brand-fg);
  font-weight: 600;
}
</style>